import {
  Box,
  IconButton,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@benchlabs/react-design-system';
import { XIcon } from '@benchlabs/react-design-system/dist/icons/phosphor/XIcon';
import { format, getMonth, getYear } from 'date-fns';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '../types';

const visuallyHidden = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden' as const,
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
};

interface SelectInputProps {
  id: keyof FormValues;
  label: string;
  value?: string;
}

export const SelectInput = ({
  id,
  label,
  ...props
}: SelectInputProps & SelectProps): React.ReactElement => {
  const { control } = useFormContext<FormValues>();
  return (
    <Box display="grid" gridTemplateColumns="1fr minmax(50px, 279px)" alignItems="center" gap="8px">
      <Typography component="label" variant="body2" htmlFor={id} id={`${id}-label`}>
        {label}
      </Typography>
      <Controller
        control={control}
        render={({ field }) => (
          <Select {...props} {...field} id={id} size="small" labelId={`${id}-label`}>
            <MenuItem value={field.value}>{field.value}</MenuItem>
          </Select>
        )}
        name={id}
      />
    </Box>
  );
};

export const MonthYearSelectInput = ({
  id,
  label,
  disabled,
}: SelectInputProps & SelectProps): React.ReactElement => {
  const { setValue, formState, clearErrors, watch } = useFormContext<FormValues>();
  const value = watch(id);
  const monthOptions = Array.from({ length: 12 }, (_, i) => i);
  const yearOptions = Array.from({ length: 101 }, (_, i) => i + 2000);
  const [selectedMonth, setSelectedMonth] = React.useState<number | undefined>(
    getPassedDateParts(value).month,
  );
  const [selectedYear, setSelectedYear] = React.useState<number | undefined>(
    getPassedDateParts(value).year,
  );

  React.useEffect(() => {
    const dateParts = getPassedDateParts(value);
    setSelectedMonth(dateParts.month);
    setSelectedYear(dateParts.year);
  }, [value]);

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(event.target.value));
    setMonthYear(Number(event.target.value), selectedYear);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
    setMonthYear(selectedMonth, Number(event.target.value));
  };

  const setMonthYear = (month?: number, year?: number) => {
    if (!month || !year) return;
    setValue(id, new Date(year, month).toISOString());
    clearErrors(id);
  };

  const handleClearInput =
    (setFunction: React.Dispatch<React.SetStateAction<number | undefined>>) => () => {
      setFunction(undefined);
    };

  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr minmax(50px, 134px) minmax(50px, 134px)"
      gap="8px"
      justifyContent="center"
      alignItems="center"
    >
      <Typography variant="body2">{label}</Typography>
      <Box position="relative">
        <FormControl fullWidth size="small" error={Boolean(formState.errors[id])}>
          <InputLabel id={`${id}-month-label`}>Month</InputLabel>
          <Typography
            component="label"
            htmlFor={`${id}-month`}
            id={`${id}-month-aria-label`}
            sx={visuallyHidden}
          >
            Month
          </Typography>
          <Select
            labelId={`${id}-month-aria-label`}
            id={`${id}-month`}
            value={selectedMonth}
            disabled={disabled}
            label="Month"
            size="small"
            onChange={handleMonthChange}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: selectedMonth ? 'none' : '',
              },
            }}
            endAdornment={
              !disabled && selectedMonth ? (
                <InputAdornment sx={{ marginRight: '-10px' }} position="end">
                  <IconButton onClick={handleClearInput(setSelectedMonth)} size="small">
                    <XIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          >
            {monthOptions.map(monthOption => (
              <MenuItem key={monthOption} value={monthOption}>
                {format(new Date(2000, monthOption), 'MMMM')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box position="relative" width="100%" display="flex" justifyContent="flex-end">
        <FormControl fullWidth size="small" error={Boolean(formState.errors[id])}>
          <InputLabel htmlFor={`${id}-year-label`}>Year</InputLabel>
          <Typography
            component="label"
            htmlFor={`${id}-year`}
            id={`${id}-year-aria-label`}
            sx={visuallyHidden}
          >
            Year
          </Typography>
          <Select
            labelId={`${id}-year-aria-label`}
            id={`${id}-year`}
            value={selectedYear}
            disabled={disabled}
            label="Year"
            size="small"
            onChange={handleYearChange}
            sx={{
              '& .MuiSelect-iconOutlined': {
                display: selectedYear ? 'none' : '',
              },
            }}
            endAdornment={
              !disabled && selectedYear ? (
                <InputAdornment sx={{ marginRight: '-10px' }} position="end">
                  <IconButton onClick={handleClearInput(setSelectedYear)} size="small">
                    <XIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          >
            {yearOptions.map(yearOption => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

interface DateParts {
  month?: number;
  year?: number;
}

function getPassedDateParts(value?: string): DateParts {
  if (!value) return {};
  // Add on dates are saved as YearMonths
  if (isYearMonth(value)) return getYearMonthParts(value);
  return getISODateParts(value);
}

function getYearMonthParts(value: string): DateParts {
  // Parse 'yyyy-MM' format manually
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return {};
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // Convert to 0-indexed month
  return { month, year };
}

function getISODateParts(value: string): DateParts {
  const date = new Date(value);
  if (isNaN(date.getTime())) return {};
  const dateWithoutOffset = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
  return getDateParts(dateWithoutOffset);
}

function getDateParts(date: Date): DateParts {
  const month = getMonth(date);
  const year = getYear(date);
  return { month, year };
}

function isYearMonth(value: string): boolean {
  return /^\d{4}-\d{2}$/.test(value);
}
